


import React from "react";
import Select from "react-select";
import { Save, X, Check } from "lucide-react";
import { TabFSUSProps } from "./types/MilestoneType";
import { Milestone, useOfflineOceanMilestones } from "./useOfflineOceanMilestones";
import { formatDateTime } from "./utils";
import { CustomOption } from "./CustomDisable";

// ── Styles ────────────────────────────────────────────────────────────────────

const s: Record<string, React.CSSProperties> = {
  root: { padding: "14px 0 0" },

  // section header
  sectionHeader: {
    display: "flex", alignItems: "center",
    justifyContent: "space-between", marginBottom: "12px",
  },
  sectionTitle: { display: "flex", alignItems: "center", gap: "7px" },
  sectionAccent: { width: "3px", height: "16px", background: "#F8B71F", borderRadius: "99px" },
  sectionLabel: {
    fontSize: "12px", fontWeight: 600, color: "#374151",
    textTransform: "uppercase" as const, letterSpacing: "0.06em",
  },
  rowCount: { fontSize: "11px", color: "#9ca3af", fontWeight: 500 },

  // filter bar
  filterBar: {
    display: "flex", alignItems: "center", gap: "10px",
    background: "#fafafa", border: "1px solid #efefef",
    borderRadius: "10px", padding: "10px 14px", marginBottom: "14px",
  },
  filterLabel: { fontSize: "11.5px", fontWeight: 600, color: "#374151", whiteSpace: "nowrap" as const },

  // active filter chip
  filterChip: {
    display: "inline-flex", alignItems: "center", gap: "6px",
    background: "#1a1a1a", color: "#fff",
    fontSize: "11.5px", fontWeight: 600,
    padding: "4px 10px", borderRadius: "6px",
    whiteSpace: "nowrap" as const,
  },
  chipDot: { width: "6px", height: "6px", borderRadius: "50%", background: "#F8B71F", flexShrink: 0 },
  chipClear: {
    background: "none", border: "none", cursor: "pointer",
    color: "#9ca3af", display: "flex", alignItems: "center",
    padding: "0", marginLeft: "2px", transition: "color 0.15s",
  },

  // container group
  containerGroup: { marginBottom: "10px" },
  containerGroupHeader: {
    display: "flex", alignItems: "center", gap: "8px",
    background: "#f7f7f7", border: "1px solid #efefef",
    borderRadius: "8px 8px 0 0",
    padding: "7px 14px", borderBottom: "none",
  },
  containerPill: {
    display: "inline-flex", alignItems: "center", gap: "5px",
    background: "#1a1a1a", color: "#fff",
    fontSize: "11px", fontWeight: 700,
    padding: "3px 9px", borderRadius: "5px",
    letterSpacing: "0.03em",
  },
  containerPillDot: { width: "5px", height: "5px", borderRadius: "50%", background: "#F8B71F" },
  containerEventCount: { fontSize: "11px", color: "#9ca3af", fontWeight: 500 },

  // column headers
  colHeaders: {
    display: "grid",
    gridTemplateColumns: "160px 130px 148px 148px 130px",
    padding: "6px 12px",
    background: "#f7f7f7",
    border: "1px solid #efefef",
    borderTop: "1px solid #e8e8e8",
    borderBottom: "none",
    minWidth: "580px",
  },
  colHeader: {
    fontSize: "10.5px", fontWeight: 600, color: "#9ca3af",
    textTransform: "uppercase" as const, letterSpacing: "0.06em", padding: "0 4px",
  },

  // event row
  eventRow: {
    display: "grid",
    gridTemplateColumns: "160px 130px 148px 148px 130px",
    background: "#fff",
    border: "1px solid #efefef",
    borderTop: "none",
    padding: "9px 12px",
    alignItems: "center",
    transition: "background 0.12s",
    minWidth: "580px",
  },
  eventRowCompleted: { background: "#f9fafb" },
  eventRowLast: { borderRadius: "0 0 8px 8px" },
  cell: { padding: "0 4px" },

  // milestone name
  milestoneName: { fontSize: "12.5px", fontWeight: 600, color: "#1a1a1a" },
  milestoneNameCompleted: { fontSize: "12.5px", fontWeight: 500, color: "#6b7280" },

  // location / vessel
  locationVal: { fontSize: "12px", color: "#374151" },
  locationNa: { fontSize: "12px", color: "#9ca3af" },
  expDate: { fontSize: "12px", color: "#374151" },
  expNa: { fontSize: "12px", color: "#9ca3af" },
  vesselVal: { fontSize: "12px", color: "#374151", fontWeight: 500 },
  vesselNa: { fontSize: "12px", color: "#9ca3af" },

  // actual date cell
  actualCell: { display: "flex", alignItems: "center", gap: "6px" },
  dateInput: {
    border: "1px solid #e2e2e2", borderRadius: "6px",
    padding: "5px 8px", fontSize: "11.5px", fontFamily: "inherit",
    color: "#374151", background: "#fff", outline: "none",
    cursor: "pointer", width: "130px",
    transition: "border-color 0.15s, box-shadow 0.15s",
  },
  dateInputDisabled: { background: "#f5f5f5", color: "#9ca3af", cursor: "not-allowed", borderColor: "#eeeeee" },

  // clear button
  clearBtn: {
    background: "#fee2e2", border: "none", borderRadius: "5px",
    width: "22px", height: "22px",
    display: "flex", alignItems: "center", justifyContent: "center",
    cursor: "pointer", flexShrink: 0, color: "#dc2626",
    transition: "background 0.15s",
  },

  // done badge
  doneBadge: {
    display: "inline-flex", alignItems: "center", gap: "4px",
    background: "#dcfce7", color: "#166534",
    fontSize: "11px", fontWeight: 600,
    padding: "3px 8px", borderRadius: "5px",
  },

  // empty state
  emptyState: {
    background: "#fafafa", border: "1px dashed #e0e0e0",
    borderRadius: "10px", padding: "40px 20px",
    textAlign: "center" as const, color: "#9ca3af",
    fontSize: "13px", fontWeight: 500, margin: "12px 0",
  },

  // footer
  footer: {
    display: "flex", justifyContent: "flex-end", alignItems: "center",
    padding: "12px 0 0", borderTop: "1px solid #f0f0f0", marginTop: "8px",
  },
  saveBtn: {
    display: "inline-flex", alignItems: "center", gap: "6px",
    background: "#F8B71F", border: "none", borderRadius: "7px",
    padding: "7px 16px", fontSize: "12.5px", fontWeight: 600,
    color: "#1a1a1a", cursor: "pointer", fontFamily: "inherit",
    boxShadow: "0 1px 4px rgba(248,183,31,0.35)", transition: "background 0.15s",
  },
  saveBtnDisabled: { background: "#e5e7eb", color: "#9ca3af", cursor: "not-allowed", boxShadow: "none" },
};

// ── react-select styles ───────────────────────────────────────────────────────

const selectStyles = {
  control: (base: any, state: any) => ({
    ...base,
    fontSize: "12px",
    minHeight: "32px",
    maxWidth: "280px",
    borderColor: state.isFocused ? "#F8B71F" : "#e2e2e2",
    boxShadow: state.isFocused ? "0 0 0 2px rgba(248,183,31,0.18)" : "none",
    "&:hover": { borderColor: "#F8B71F" },
    borderRadius: "6px",
    background: "#fff",
  }),
  option: (base: any, state: any) => ({
    ...base, fontSize: "12px",
    background: state.isSelected ? "#F8B71F" : state.isFocused ? "#fef9c3" : "#fff",
    color: state.isSelected ? "#1a1a1a" : "#374151",
  }),
  placeholder: (base: any) => ({ ...base, fontSize: "12px", color: "#9ca3af" }),
  menu: (base: any) => ({ ...base, borderRadius: "8px", boxShadow: "0 4px 16px rgba(0,0,0,0.1)", zIndex: 20 }),
  valueContainer: (base: any) => ({ ...base, padding: "2px 8px" }),
  indicatorSeparator: () => ({ display: "none" }),
  dropdownIndicator: (base: any) => ({ ...base, padding: "4px" }),
  clearIndicator: (base: any) => ({ ...base, color: "#9ca3af", padding: "4px", "&:hover": { color: "#374151" } }),
};

// ── Helpers ───────────────────────────────────────────────────────────────────

const toInputDate = (value?: string): string => {
  if (!value) return "";
  return value.includes("T") ? value.split("T")[0] : value;
};

const COLS = ["Milestone", "Location", "Expected", "Actual", "Vessel"];

// ── Sub-components ────────────────────────────────────────────────────────────

function SectionHeader({ label, meta }: { label: string; meta?: string }) {
  return (
    <div style={s.sectionHeader}>
      <div style={s.sectionTitle}>
        <div style={s.sectionAccent} />
        <span style={s.sectionLabel}>{label}</span>
      </div>
      {meta && <span style={s.rowCount}>{meta}</span>}
    </div>
  );
}

// ── Main Component ────────────────────────────────────────────────────────────

export function TabFsusOfflineOcean({ awbNumber }: TabFSUSProps) {
  const {
    uniqueMilestones,
    containerWiseData,
    selectMilestone,
    updateDate,
    removeDate,
    saveMilestones,
    loading,
    isSelected,
  } = useOfflineOceanMilestones(awbNumber);

  // Derived counts for meta label
  const containerCount = Object.keys(containerWiseData).length;
  const eventCount = Object.values(containerWiseData).reduce((acc, evts) => acc + evts.length, 0);

  // Active milestone label (for chip)
  const [activeLabel, setActiveLabel] = React.useState<string | null>(null);

  const handleMilestoneChange = (option: any) => {
    selectMilestone(option?.item);
    setActiveLabel(option?.label ?? null);
  };

  const handleClearMilestone = () => {
    // selectMilestone([]);
    setActiveLabel(null);
  };

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div style={s.root}>

      <SectionHeader
        label="Vessel Updates"
        meta={containerCount > 0 ? `${containerCount} container${containerCount !== 1 ? "s" : ""} · ${eventCount} events` : undefined}
      />

      {/* Milestone filter bar */}
      <div style={s.filterBar}>
        <span style={s.filterLabel}>Filter by milestone</span>

        {activeLabel ? (
          // Active chip
          <div style={s.filterChip}>
            <span style={s.chipDot} />
            {activeLabel}
            <button style={s.chipClear} onClick={handleClearMilestone} title="Clear filter">
              <X size={11} />
            </button>
          </div>
        ) : (
          // Dropdown
          // <Select
          //   isClearable
          //   styles={selectStyles}
          //   options={uniqueMilestones.map((item: Milestone) => ({
          //     label: item.mileStone,
          //     value: item.mileStone,
          //     item,
          //   }))}
          //   placeholder="Select milestone…"
          //   onChange={handleMilestoneChange}
          // />

          // <Select
          //   isClearable
          //   styles={selectStyles}
          //   options={uniqueMilestones.map((item: Milestone, index: number) => {
          //     // Check if all previous milestones have status === 1
          //     const isPreviousComplete = uniqueMilestones
          //       .slice(0, index)
          //       .every((prev: Milestone) => prev.status === 1);

          //     return {
          //       label: item.mileStone,
          //       value: item.mileStone,
          //       item,
          //       isDisabled: !isPreviousComplete, // ✅ disable if any previous is not done
          //     };
          //   })}
          //   placeholder="Select milestone…"
          //   onChange={handleMilestoneChange}
          // />

          <Select
            isClearable
            styles={selectStyles}
            components={{ Option: CustomOption }}   // ✅ add this
            options={uniqueMilestones.map((item: Milestone, index: number) => {
              const previousMilestones = uniqueMilestones.slice(0, index);
              const isPreviousComplete = previousMilestones.every(
                (prev: Milestone) => prev.status === 1
              );

              // Find the first incomplete previous milestone to show in tooltip
              const firstIncomplete = previousMilestones.find(
                (prev: Milestone) => prev.status !== 1
              );

              return {
                label: item.mileStone,
                value: item.mileStone,
                item,
                isDisabled: !isPreviousComplete,
                blockingMilestone: firstIncomplete?.mileStone ?? null, // ✅ which one is blocking
              };
            })}
            placeholder="Select milestone…"
            onChange={handleMilestoneChange}
          />
        )}
      </div>

      {/* Content */}
      {containerCount === 0 ? (
        <div style={s.emptyState}>
          <span>🚢</span>
          No status currently available
        </div>
      ) : (
        <div style={{ overflowX: "auto" }}>
          {Object.entries(containerWiseData).map(([containerNumber, events]) => {
            const lastIdx = events.length - 1;
            return (
              <div key={containerNumber} style={s.containerGroup}>

                {/* Container header */}
                <div style={s.containerGroupHeader}>
                  <div style={s.containerPill}>
                    <span style={s.containerPillDot} />
                    {containerNumber}
                  </div>
                  <span style={s.containerEventCount}>
                    {events.length} event{events.length !== 1 ? "s" : ""}
                  </span>
                </div>

                {/* Column headers */}
                <div style={s.colHeaders}>
                  {COLS.map(h => <span key={h} style={s.colHeader}>{h}</span>)}
                </div>

                {/* Event rows */}
                {events.map((event, index) => {
                  const isCompleted = event.status === 1;
                  const isLast = index === lastIdx;
                  const rowStyle: React.CSSProperties = {
                    ...s.eventRow,
                    ...(isCompleted ? s.eventRowCompleted : {}),
                    ...(isLast ? s.eventRowLast : {}),
                  };

                  return (
                    <div key={`${containerNumber}-${index}`} style={rowStyle}>

                      {/* Milestone */}
                      <div style={s.cell}>
                        <span style={isCompleted ? s.milestoneNameCompleted : s.milestoneName}>
                          {event.mileStone}
                        </span>
                      </div>

                      {/* Location */}
                      <div style={s.cell}>
                        {event.portName || event.location
                          ? <span style={s.locationVal}>{event.portName || event.location}</span>
                          : <span style={s.locationNa}>—</span>
                        }
                      </div>

                      {/* Expected */}
                      <div style={s.cell}>
                        {event.expectedDate
                          ? <span style={s.expDate}>{formatDateTime(event.expectedDate)}</span>
                          : <span style={s.expNa}>Not available</span>
                        }
                      </div>

                      {/* Actual date */}
                      <div style={s.cell}>
                        <div style={s.actualCell}>
                          <input
                            type="date"
                            style={{
                              ...s.dateInput,
                              ...((!isSelected || isCompleted) ? s.dateInputDisabled : {}),
                            }}
                            value={toInputDate(event.date)}
                            disabled={!isSelected || isCompleted}
                            onMouseDown={(e: any) => { e.preventDefault(); e.target.showPicker(); }}
                            onChange={e => updateDate(containerNumber, index, e.target.value)}
                          />

                          {/* Clear button — only for pending rows with a date */}
                          {event.date && isSelected && !isCompleted && (
                            <button
                              style={s.clearBtn}
                              onClick={() => removeDate(containerNumber, index)}
                              title="Clear date"
                            >
                              <X size={10} />
                            </button>
                          )}


                          {/* {isCompleted && (
                            <span style={s.doneBadge}>
                              <Check size={10} />
                              Done
                            </span>
                          )} */}
                        </div>
                      </div>

                      {/* Vessel */}
                      <div style={s.cell}>
                        {event.vesselName
                          ? <span style={s.vesselVal}>{event.vesselName}</span>
                          : <span style={s.vesselNa}>—</span>
                        }
                      </div>

                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
      )}

      {/* Save footer — only when milestone is selected */}
      {isSelected && (
        <div style={s.footer}>
          <button
            style={{ ...s.saveBtn, ...(loading ? s.saveBtnDisabled : {}) }}
            disabled={loading}
            onClick={saveMilestones}
          >
            <Save size={13} />
            {loading ? "Saving…" : "Save Updates"}
          </button>
        </div>
      )}

    </div>
  );
}
